
import chargersData from '../data/chargers.json';

// --- Constants for calculations ---
const COST_PER_METER = 40; // EUR per meter for installation
const BASE_INSTALLATION_COST = 600; // EUR
const ROADWORK_COST = 1200; // EUR if roadwork is needed

const KWH_PRICE = 0.25; // EUR per kWh
const AVG_FUEL_PRICE_PER_LITER = 1.9; // EUR
const AVG_COMBUSTION_CAR_CONSUMPTION_L_PER_100KM = 7; // L/100km
const AVG_EV_CONSUMPTION_KWH_PER_100KM = 18; // kWh/100km

// --- Grant Logic ---
const calculateGrants = (totalCostBeforeGrants, formData) => {
  let advenirAmount = 0;
  
  // ADVENIR grant for condominiums
  if (formData.parkingLocation === 'Copropriété') {
    advenirAmount = Math.min(totalCostBeforeGrants * 0.5, 960);
  }

  // Tax credit
  const taxCreditAmount = Math.min(totalCostBeforeGrants * 0.75, 500);

  return { advenirAmount, taxCreditAmount };
};

// --- Main Calculation Function ---
export const calculateSimulation = (formData) => {
  // 1. Recommend a charger
  const desiredPower = parseFloat(formData.desiredPower);
  const isThreePhase = formData.meterType === 'Triphasé';

  let recommendedCharger = chargersData.find(c => 
    c.power_kw === desiredPower && (isThreePhase ? c.power_kw >= 11 : c.power_kw <= 7.4)
  );
  // Fallback to the closest power
  if (!recommendedCharger) {
    recommendedCharger = [...chargersData]
      .sort((a, b) => Math.abs(a.power_kw - desiredPower) - Math.abs(b.power_kw - desiredPower))
      .find(c => (isThreePhase ? c.power_kw >= 11 : c.power_kw <= 7.4));
  }
  // Final fallback
  if (!recommendedCharger) recommendedCharger = chargersData[0];

  // 2. Financial Estimation
  const chargerCost = recommendedCharger.price_eur;
  const installationCost = BASE_INSTALLATION_COST + (parseInt(formData.distanceToPanel, 10) * COST_PER_METER);
  const additionalWorkCost = formData.roadworkNeeded === 'Oui' ? ROADWORK_COST : 0;
  
  // Apply reduced VAT if applicable (e.g., individual house)
  const isReducedVat = formData.parkingLocation === 'Maison individuelle';
  const vatRate = isReducedVat ? 0.055 : 0.20;
  const totalBeforeGrants = (chargerCost + installationCost + additionalWorkCost) * (1 + vatRate);

  const { advenirAmount, taxCreditAmount } = calculateGrants(totalBeforeGrants, formData);
  const totalNetCost = totalBeforeGrants - advenirAmount - taxCreditAmount;

  // 3. Technical Estimation
  const chargeTimeHours = formData.batteryCapacity && recommendedCharger.power_kw 
    ? parseFloat(formData.batteryCapacity) / recommendedCharger.power_kw 
    : 0;
  
  const dailyKwh = (parseFloat(formData.dailyMileage) / 100) * AVG_EV_CONSUMPTION_KWH_PER_100KM;
  const monthlyConsumptionKwh = dailyKwh * 30;

  const annualEvCost = (dailyKwh * 365) * KWH_PRICE;
  const annualFuelCost = ((parseFloat(formData.dailyMileage) * 365) / 100) * AVG_COMBUSTION_CAR_CONSUMPTION_L_PER_100KM * AVG_FUEL_PRICE_PER_LITER;
  const annualSavings = annualFuelCost - annualEvCost;

  return {
    financial: {
      chargerCost,
      installationCost,
      additionalWorkCost,
      totalBeforeGrants,
      advenirAmount,
      taxCreditAmount,
      totalNetCost,
      vatRate,
    },
    technical: {
      recommendedCharger,
      chargeTimeHours: chargeTimeHours.toFixed(1),
      monthlyConsumptionKwh: monthlyConsumptionKwh.toFixed(0),
      annualSavings: annualSavings.toFixed(0),
    },
    recommendations: {
      optimalPower: `La puissance de ${recommendedCharger.power_kw} kW est un excellent choix pour votre usage.`,
      additionalOptions: recommendedCharger.features,
      normCompliance: "Toutes nos installations respectent la norme NF C 15-100.",
    }
  };
};
